import {useContainer, useRoute} from '#imports';
import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import type {PageModel} from '~~/package/mapper/common/model/PageModel';
import type ProSkillAssureMapper from '~~/package/mapper/proSkillAssure/ProSkillAssureMapper';
import type {ProSkillAssureTrackModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureTrackModel';
import type {TaskModel} from '~~/package/mapper/learning/model/TaskModel';
import {
    ProSkillAssureAnswerCodeModel
} from '~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeModel';
import {
    ProSkillAssureAssessmentMyInfoModel
} from '~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel';
import {ProSkillAssureMyAssessmentModel} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureMyAssessmentModel';
import {
    ProSkillAssureAssessmentDetailModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDetailModel';
import type {
    ProSkillAssureAssessmentDataModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentDataModel';
import {
    ProSkillAssureAssessmentInfoModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureAssessmentInfoModel';
import type {
    ProSkillAssureAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentModel';
import type ProSkillAssureCreateRequest from '~~/package/apis/ProSkillAssure/request/ProSkillAssureCreateRequest';
import {LevelAssessmentResultModel} from '~~/package/mapper/levelAssessment/model/LevelAssessmentResultModel';
import {CautionType} from '~~/package/types/CautionType';
import type ProSkillAssureTimeZoneResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureTimeZoneResponse';
import {useLearningStore} from '~/store/learning/learningStore';
import {
    assureAssessmentData,
    assureAssessmentInfo
} from '~~/application/components/ProSkillAssure/settings/sample/ProSkillAssureSampleData';
import {useContestService} from '~/composables/service/useContestService';
import {useLearningService} from '~/composables/service/useLearningService';
import {TaskType} from '~~/package/mapper/learning/model/TaskType';
import {
    ProSkillAssureTemplateCreateModel
} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateCreateModel';
import {ProSkillAssureTemplateModel} from '~~/package/mapper/proSkillAssure/model/template/ProSkillAssureTemplateModel';
import type {
    ProSkillAssureSampleAssessmentModel
} from '~~/package/mapper/proSkillAssure/model/ProSkillAssureSampleAssessmentModel';
import type ProSkillAssureCandidateValidateResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureCandidateValidateResponse';
import type ProSkillAssureEnvironmentReportRequest
    from '~~/package/apis/ProSkillAssure/request/ProSkillAssureEnvironmentReportRequest';
import {collectClientEnvironment} from '~~/package/utils/ClientEnvironmentUtil';
import type ProSkillAssureAiRecommendEntryResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendEntryResponse';
import type ProSkillAssureAiRecommendResponse
    from '~~/package/apis/ProSkillAssure/response/ProSkillAssureAiRecommendResponse';
import type ProSkillAssureAiRecommendRequest
    from '~~/package/apis/ProSkillAssure/request/ProSkillAssureAiRecommendRequest';

export const useProSkillAssureService = () => {
    const proSkillAssureMapper = useContainer<ProSkillAssureMapper>(DependencyInjectId.ProSkillAssureMapper);
    const contestService = useContestService();
    const learningStore = useLearningStore();
    const route = useRoute();

    const getProSkillAssessmentTrack = (page: number, itemSize: number, categoryList?: string[], field?: string, type?: string[], trackTitle?: string): Promise<PageModel<ProSkillAssureTrackModel>> => {
        return proSkillAssureMapper.getProSkillAssessmentTrack(page, itemSize, categoryList, field, type, trackTitle);
    };

    const getAssessment = (assessmentId: number): Promise<ProSkillAssureAssessmentModel> => {
        return proSkillAssureMapper.getProSkillAssureAssessment(assessmentId);
    };

    const getAssessmentRemainingTime = (assessmentId: number): Promise<number> => {
        return proSkillAssureMapper.getAssessmentRemainingTime(assessmentId);
    }

    const getAssessmentTask = (assessmentId: number, testTemplateId: number, taskId: number): Promise<TaskModel> => {
        return proSkillAssureMapper.getProSkillAssureAssessmentTask(assessmentId, testTemplateId, taskId)
    }

    const getAssessmentTasksWithLatestCodes = async (assessmentId: number, testTemplateId:number, taskId: number): Promise<TaskModel> => {
        const taskData = getAssessmentTask(assessmentId, testTemplateId, taskId)

        if (([TaskType.CODE, TaskType.MULTI_FILE_CODE, TaskType.CODE_WITHOUT_RUN] as TaskType[]).includes((await taskData).type as TaskType))
            (await taskData).submitCode = await useLearningService().getLatestSavedCode(taskId, "custom_assessment", assessmentId);

        return taskData;
    }

    const requestAnswer = (assessmentId: number, testTemplateId: number, taskId: number, answer: ProSkillAssureAnswerCodeModel): Promise<boolean> => {
        return proSkillAssureMapper.requestAnswer(assessmentId, testTemplateId, taskId, answer, learningStore.tokenData)
    }

    const updateRulesAgreeFlag = (assessmentId: number, rulesAgreeFlag: boolean): Promise<boolean> => {
        return proSkillAssureMapper.updateRulesAgreeFlag(assessmentId, rulesAgreeFlag);
    }

    const updateStatus = (assessmentId: number, status: "COMPLETED" | "START"): Promise<string | null> => {
        return proSkillAssureMapper.updateStatus(assessmentId, status);
    }

    const getProSkillAssureMyInfo = (assessmentId: number): Promise<ProSkillAssureAssessmentMyInfoModel> => {
        return proSkillAssureMapper.getProSkillAssureMyInfo(assessmentId);
    }

    /**
     * ASMT-PROCTOR-16 (TSK-11692): 진입 차단 / 점검 실패를 운영팀에 알린다.
     * 알림은 부수적 신호일 뿐이므로 실패해도 삼킨다 — 응시자 흐름을 절대 막지 않는다.
     *
     * REQ-313 추가 요구: 접속 환경(브라우저·기기/OS)은 여기서 한 번에 붙인다. 호출부(차단, 기본·강화
     * 감독 점검 실패)마다 챙기게 두면 새 경로가 생길 때 빠진다.
     */
    const reportEnvironmentEvent = async (assessmentId: number, request: ProSkillAssureEnvironmentReportRequest): Promise<void> => {
        try {
            const clientEnvironment = await collectClientEnvironment();
            await proSkillAssureMapper.reportEnvironmentEvent(assessmentId, { ...request, clientEnvironment });
        } catch (e) {
            console.warn('[useProSkillAssureService] 응시 환경 이벤트 알림 실패 (무시)', e);
        }
    }

    const getProSkillAssureAssessmentList = (page: number, itemSize: number, filters?: string[], assessmentTitle?: string): Promise<PageModel<ProSkillAssureAssessmentDataModel>> => {
        return proSkillAssureMapper.getProSkillAssureAssessmentList(page, itemSize, filters, assessmentTitle);
    }

    const getMyAssessmentList = (page?: number, size?: number, status?: string[], isAdmin?: boolean): Promise<PageModel<ProSkillAssureMyAssessmentModel>> => {
        return proSkillAssureMapper.getMyAssessmentList(page, size, status, isAdmin);
    };

    const getSampleAssessmentList = (lang: string, page: number, size: number): Promise<PageModel<ProSkillAssureSampleAssessmentModel>> => {
        return proSkillAssureMapper.getSampleAssessmentList(lang, page, size);
    };

    const getProSkillAssureAssessmentInfo = (customAssessmentId: number): Promise<ProSkillAssureAssessmentInfoModel> => {
        if(customAssessmentId === 0) {
            return Promise.resolve(assureAssessmentInfo)
        }
        else return proSkillAssureMapper.getProSkillAssureAssessmentInfo(customAssessmentId);
    }

    const getProSkillAssureAssessmentDetail = (customAssessmentId: number): Promise<ProSkillAssureAssessmentDetailModel> => {
        if(customAssessmentId === 0) {
            return Promise.resolve(assureAssessmentData)
        }
        else return proSkillAssureMapper.getProSkillAssureAssessmentDetail(customAssessmentId);
    }

    const deleteProSkillAssureCandidates = (customAssessmentId: number, userIds: number[]): Promise<boolean> => {
        return proSkillAssureMapper.deleteProSkillAssureCandidates(customAssessmentId, userIds);
    }

    const setProSkillAssureAssessment = (assessmentSets: ProSkillAssureCreateRequest): Promise<number> => {
        return proSkillAssureMapper.setProSkillAssureAssessment(assessmentSets);
    }

    const addProSkillAssureCandidateToAssessment = (emailList: string[], assessmentId: number): Promise<boolean> => {
        return proSkillAssureMapper.addProSkillAssureCandidateToAssessment(emailList, assessmentId);
    }

    const validateProSkillAssureCandidates = (assessmentId: number, emailList: string[]): Promise<ProSkillAssureCandidateValidateResponse> => {
        return proSkillAssureMapper.validateProSkillAssureCandidates(assessmentId, emailList);
    }

    const updateProSkillAssureAssessment = (customAssessmentId: number, assessment: ProSkillAssureCreateRequest): Promise<boolean> => {
        return proSkillAssureMapper.updateProSkillAssureAssessment(customAssessmentId, assessment);
    }

    const deleteProSkillAssureAssessment = (customAssessmentId: number): Promise<boolean> => {
        return proSkillAssureMapper.deleteProSkillAssureAssessment(customAssessmentId);
    }

    const getProSkillAssureAssessmentSampleList = (testTemplateId: number): Promise<LevelAssessmentResultModel[]> => {
        return proSkillAssureMapper.getProSkillAssureAssessmentSampleList(testTemplateId);
    }

    const addProSkillAssureAssessmentSaveCandidateAction = (customAssessmentId: number, suspiciousAction: CautionType, actionContent?: string): Promise<number> => {
        return proSkillAssureMapper.addProSkillAssureAssessmentSaveCandidateAction(customAssessmentId, suspiciousAction, actionContent)
    }

    const getTimezoneList = (): Promise<ProSkillAssureTimeZoneResponse[]> => {
        return proSkillAssureMapper.getTimezoneList();
    }

    const getEmbedUrl = async (): Promise<string> => {
        const taskId = learningStore.task.id;

        if(learningStore.isProSkillAssure) {
            const task = await getAssessmentTask(parseInt(route.params.assessmentId as string), learningStore.getCurrentProSkillAssureSet(taskId).templateId, taskId);
            return task.embedUrl;
        } else if(learningStore.isContest) {
            const contestData = await contestService.getContestData(parseInt(route.params.contestId as string));
            const taskId = parseInt(route.params.taskId as string);

            const embedUrlList = contestData.contestTaskList.filter(task => task.id === taskId).map(task => task.embedUrl);
            if(embedUrlList.length > 0) return embedUrlList[0];
        }

        return "";
    }

    // Re-mint the embed URL (fresh B-handle) for the CURRENT assessment task, WITHOUT reloading task
    // data (unlike getEmbedUrl, which pulls the whole task VO). Used on (re)open of an agent-prompt lab
    // so the proxy exchange gets a live handle and the USD budget applies. Empty string when not an
    // assessment or on failure (caller fails open to the cached URL).
    const refreshEmbedUrl = async (): Promise<string> => {
        const taskId = learningStore.task.id;
        if(learningStore.isProSkillAssure) {
            return proSkillAssureMapper.getProSkillAssureEmbedUrl(parseInt(route.params.assessmentId as string), learningStore.getCurrentProSkillAssureSet(taskId).templateId, taskId);
        }
        return "";
    }

    const getProSkillAssureTemplate = (page: number, itemSize: number, lang: string, title?: string): Promise<PageModel<ProSkillAssureTemplateModel>> => {
        return proSkillAssureMapper.getProSkillAssureTemplate(page, itemSize, lang, title)
    }

    const addProSkillAssureTemplate = (request: ProSkillAssureTemplateCreateModel): Promise<boolean> => {
        return proSkillAssureMapper.addProSkillAssureTemplate(request);
    }

    const modifyProSkillAssureTemplate = (title: string, assessmentTemplateId: number): Promise<boolean> => {
        return proSkillAssureMapper.modifyProSkillAssureTemplate(title, assessmentTemplateId);
    }

    const removeProSkillAssureTemplate = (assessmentTemplateId: number): Promise<boolean> => {
        return proSkillAssureMapper.removeProSkillAssureTemplate(assessmentTemplateId);
    }

    const getAiRecommendEntry = (): Promise<ProSkillAssureAiRecommendEntryResponse> => {
        return proSkillAssureMapper.getAiRecommendEntry();
    }

    const requestAiRecommend = (request: ProSkillAssureAiRecommendRequest): Promise<ProSkillAssureAiRecommendResponse> => {
        return proSkillAssureMapper.requestAiRecommend(request);
    }

    return {
        getProSkillAssessmentTrack,
        getAssessment,
        getAssessmentRemainingTime,
        getAssessmentTask,
        getAssessmentTasksWithLatestCodes,
        requestAnswer,
        updateRulesAgreeFlag,
        updateStatus,
        getProSkillAssureMyInfo,
        reportEnvironmentEvent,
        getProSkillAssureAssessmentList,
        getMyAssessmentList,
        getSampleAssessmentList,
        getProSkillAssureAssessmentInfo,
        getProSkillAssureAssessmentDetail,
        deleteProSkillAssureCandidates,
        setProSkillAssureAssessment,
        addProSkillAssureCandidateToAssessment,
        validateProSkillAssureCandidates,
        updateProSkillAssureAssessment,
        deleteProSkillAssureAssessment,
        getProSkillAssureAssessmentSampleList,
        addProSkillAssureAssessmentSaveCandidateAction,
        getTimezoneList,
        getEmbedUrl,
        refreshEmbedUrl,
        getProSkillAssureTemplate,
        addProSkillAssureTemplate,
        modifyProSkillAssureTemplate,
        removeProSkillAssureTemplate,
        getAiRecommendEntry,
        requestAiRecommend
    };
}
